import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../../firebase";
import { AppContext } from "../../store/AppContext";
import { apiFetch } from "../../utils/api";
import "./CustomerEngagementTab.css";

const TABS = [
  "Overview",
  "WhatsApp Campaigns",
  "Abandoned Cart",
  "Templates",
  "Audience",
  "Message Logs",
  "Settings",
];

const DATE_WINDOW_OPTIONS = [7, 14, 30, 90, 180, 365];

const AUDIENCE_PRESETS = [
  { value: "all", label: "All customers" },
  { value: "imported", label: "Imported Excel audience" },
  { value: "new", label: "New customers" },
  { value: "existing", label: "Existing customers" },
  { value: "high_value", label: "High-value customers" },
  { value: "inactive", label: "Haven't purchased recently" },
  { value: "abandoned_cart", label: "Customers with abandoned carts" },
  { value: "whatsapp_received", label: "Previously received WhatsApp" },
];

const INITIAL_CUSTOMER_LIMIT = 1000;
const INITIAL_CART_LIMIT = 500;
const INITIAL_ORDER_LIMIT = 500;

const isApprovedTemplate = (template) =>
  ["approved", "active", "active - quality pending"].includes(
    String(template?.approvalStatus || template?.status || "")
      .trim()
      .toLowerCase(),
  );

const DEFAULT_CAMPAIGN_TEMPLATE_BODY =
  "Hello {{1}}, Kathy's has something selected for you.\n\n{{2}}\n\nShop here: {{3}}\n\nReply STOP to opt out.";

const META_ACTIVE_STATUS = "active - quality pending";

const DEFAULT_WHATSAPP_TEMPLATES = [
  {
    id: "meta_kathys_campaign_image",
    name: "kathys_campaign_image",
    category: "Marketing",
    language: "English",
    body: DEFAULT_CAMPAIGN_TEMPLATE_BODY,
    variables: "customer_name, campaign_message, destination_url",
    hasImageHeader: true,
    approvalStatus: META_ACTIVE_STATUS,
    source: "meta_default",
  },
  {
    id: "meta_kathys_order_status",
    name: "kathys_order_status",
    category: "Utility",
    language: "English",
    body: "Hi {{1}}, your Kathy's order {{2}} has an update.\n\nStatus: {{3}}\nItems total: {{4}}\nDelivery charge: {{5}}\nGrand total: {{6}}\nPayment method: {{7}}\nTracking: {{8}}\n\nThank you for shopping with Kathy's.",
    variables:
      "customer_name, order_id, status, items_total, delivery_charge, grand_total, payment_method, tracking",
    hasImageHeader: false,
    approvalStatus: META_ACTIVE_STATUS,
    source: "meta_default",
  },
  {
    id: "meta_kathys_order_received",
    name: "kathys_order_received",
    category: "Utility",
    language: "English",
    body: "Hi {{1}}, your Kathy's order {{2}} has been received.\n\nStatus: {{3}}\nItems total: {{4}}\nDelivery charge: {{5}}\nGrand total: {{6}}\nPayment method: {{7}}\n\nThank you for shopping with Kathy's.",
    variables:
      "customer_name, order_id, status, items_total, delivery_charge, grand_total, payment_method",
    hasImageHeader: false,
    approvalStatus: META_ACTIVE_STATUS,
    source: "meta_default",
  },
];

const mergeDefaultTemplates = (firestoreTemplates) => {
  const templatesByName = new Map(
    firestoreTemplates.map((template) => [
      String(template.name || "")
        .trim()
        .toLowerCase(),
      template,
    ]),
  );

  DEFAULT_WHATSAPP_TEMPLATES.forEach((metaTemplate) => {
    const key = metaTemplate.name.toLowerCase();
    const storedTemplate = templatesByName.get(key);

    // These templates are confirmed active in Meta. Keep a Firestore document ID
    // when one exists, but do not let stale local status/body fields hide them.
    templatesByName.set(key, {
      ...storedTemplate,
      ...metaTemplate,
      id: storedTemplate?.id || metaTemplate.id,
      source: storedTemplate ? "firestore_meta_confirmed" : metaTemplate.source,
    });
  });

  return Array.from(templatesByName.values());
};

const DEFAULT_ABANDONED_STEPS = [
  {
    delayValue: 1,
    delayUnit: "hours",
    templateName: "kathys_abandoned_cart",
    enabled: true,
    discountCode: "",
  },
  {
    delayValue: 12,
    delayUnit: "hours",
    templateName: "kathys_abandoned_cart_second",
    enabled: true,
    discountCode: "",
  },
  {
    delayValue: 24,
    delayUnit: "hours",
    templateName: "kathys_abandoned_cart_discount",
    enabled: false,
    discountCode: "",
  },
];

const emptyCampaign = {
  name: "",
  templateId: "",
  audiencePreset: "all",
  status: "draft",
  sendMode: "now",
  scheduledAt: "",
  targetType: "general",
  targetProductId: "",
  targetCategoryId: "",
  targetUrl: "",
  campaignMessage: "",
  creativeImageUrl: "",
  importAudienceId: "",
  variables: "customer_name, campaign_message, destination_url",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  return new Date(value);
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isValidHttpsUrl = (value) => {
  try {
    return new URL(String(value || "").trim()).protocol === "https:";
  } catch {
    return false;
  }
};

const engagementRequest = async (path, options) => {
  const response = await apiFetch(path, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Engagement request failed");
  return data;
};

const getCustomerName = (customer) =>
  [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
  customer.displayName ||
  customer.name ||
  customer.email ||
  customer.id;

const getCustomerPhone = (customer) =>
  customer.phone ||
  customer.mobileNumber ||
  customer.customer_details?.mobile_number ||
  "";

const getOrderPhone = (order) =>
  String(order.customer_details?.mobile_number || "")
    .replace(/\D/g, "")
    .slice(-10);

const getUserPhone = (customer) =>
  String(getCustomerPhone(customer) || "")
    .replace(/\D/g, "")
    .slice(-10);

const getProductName = (product) =>
  product?.product_name ||
  product?.name ||
  product?.title ||
  "Selected product";

const getProductPrice = (product) =>
  product?.discounted_price ||
  product?.discountPrice ||
  product?.product_discounted_price ||
  product?.price ||
  product?.product_price;

const getPreviewImage = (item) =>
  item?.images?.[0] || item?.image || item?.imageUrl || item?.thumbnail || "";

const asOptInFlag = (value) => {
  if (value === true) return true;
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return ["true", "yes", "y", "1", "opted in"].includes(normalized);
};

const StatusPill = ({ value }) => {
  const normalized = String(value || "unknown")
    .trim()
    .toLowerCase();
  const color = [
    "approved",
    "active",
    "active - quality pending",
    "completed",
    "delivered",
    "read",
    "sent",
    "sent_to_meta",
  ].includes(normalized)
    ? "success"
    : ["failed", "rejected", "cancelled", "delivery_failed"].includes(
          normalized,
        )
      ? "error"
      : [
            "pending",
            "scheduled",
            "queued",
            "processing",
            "retry_pending",
          ].includes(normalized)
        ? "warning"
        : "default";

  return (
    <Chip
      label={normalized.replace(/_/g, " ")}
      color={color}
      size="small"
      variant={color === "default" ? "outlined" : "filled"}
      sx={{ textTransform: "capitalize", fontWeight: 400 }}
    />
  );
};

const CustomerEngagementTab = () => {
  const { selectedTab, setHeaderData, setLoader } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("Overview");
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [carts, setCarts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState(DEFAULT_WHATSAPP_TEMPLATES);
  const [logs, setLogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [importedAudiences, setImportedAudiences] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importName, setImportName] = useState("");
  const [importContacts, setImportContacts] = useState([]);
  const [settings, setSettings] = useState({
    frequencyLimitDays: 7,
    respectOptOut: false,
    abandonedCartEnabled: true,
    abandonedCartTriggerValue: 60,
    abandonedCartTriggerUnit: "minutes",
    steps: DEFAULT_ABANDONED_STEPS,
  });
  const [campaignForm, setCampaignForm] = useState(emptyCampaign);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: "kathys_campaign_image",
    category: "Marketing",
    language: "English",
    header: "Image",
    body: DEFAULT_CAMPAIGN_TEMPLATE_BODY,
    footer: "Kathy's Clothing Store",
    buttons: "",
    variables: "customer_name, campaign_message, destination_url",
    hasImageHeader: true,
    approvalStatus: "pending",
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sinceDays, setSinceDays] = useState(7);
  const [audiencePage, setAudiencePage] = useState(0);
  const [audienceRowsPerPage, setAudienceRowsPerPage] = useState(100);
  const [campaignPage, setCampaignPage] = useState(0);
  const [campaignRowsPerPage, setCampaignRowsPerPage] = useState(25);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(25);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionInProgress, setActionInProgress] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [campaignToCancel, setCampaignToCancel] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [summary, setSummary] = useState({
    totalCustomers: 0,
    totalCampaigns: 0,
    queuedRecipients: 0,
    sentMessages: 0,
    deliveredMessages: 0,
    readMessages: 0,
    failedMessages: 0,
    totalCampaignRevenue: 0,
    abandonedCartCandidates: 0,
    ordersWithCart: 0,
  });
  const [audienceCounts, setAudienceCounts] = useState({});
  const [loadedAudiencePresets, setLoadedAudiencePresets] = useState({});
  const [isAudienceEstimateLoading, setIsAudienceEstimateLoading] =
    useState(false);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [logsTotal, setLogsTotal] = useState(0);

  const approvedTemplates = useMemo(
    () => templates.filter(isApprovedTemplate),
    [templates],
  );
  const campaignTemplates = useMemo(
    () =>
      approvedTemplates.filter(
        (template) =>
          String(template.category || "").toLowerCase() === "marketing",
      ),
    [approvedTemplates],
  );

  const ordersByUser = useMemo(() => {
    const map = new Map();
    orders.forEach((order) => {
      if (order.userId) {
        map.set(order.userId, [...(map.get(order.userId) || []), order]);
      }
      const orderPhone = getOrderPhone(order);
      if (orderPhone) {
        map.set(orderPhone, [...(map.get(orderPhone) || []), order]);
      }
    });
    return map;
  }, [orders]);

  const cartAudienceLookup = useMemo(() => {
    const userIds = new Set();
    const cartIds = new Set();
    const phones = new Set();

    carts.forEach((cart) => {
      if (cart.userId) userIds.add(cart.userId);
      if (cart.id) cartIds.add(cart.id);
      const phone = String(cart.phone || cart.mobile || cart.mobileNumber || "")
        .replace(/\D/g, "")
        .slice(-10);
      if (phone) phones.add(phone);
    });

    return { userIds, cartIds, phones };
  }, [carts]);

  const customerMetrics = useMemo(
    () =>
      customers.map((customer) => {
        const userOrders = [
          ...(ordersByUser.get(customer.id) || []),
          ...(ordersByUser.get(getUserPhone(customer)) || []),
        ];
        const uniqueOrders = Array.from(
          new Map(userOrders.map((order) => [order.id, order])).values(),
        );
        const spend = uniqueOrders.reduce(
          (sum, order) =>
            sum + Number(order.items_total || order.orderDetails?.amount || 0),
          0,
        );
        const lastOrder = uniqueOrders
          .map((order) => toDate(order.createdAt || order.timestamp))
          .filter(Boolean)
          .sort((a, b) => b - a)[0];

        return {
          ...customer,
          totalOrders: uniqueOrders.length,
          totalSpend: spend,
          lastOrderDate: lastOrder || null,
          whatsappOptIn:
            asOptInFlag(customer.whatsappOptIn) ||
            asOptInFlag(customer.whatsapp_opt_in) ||
            asOptInFlag(customer.marketingConsent?.whatsapp) ||
            asOptInFlag(customer.communicationPreferences?.whatsapp),
        };
      }),
    [customers, ordersByUser],
  );

  const logStats = useMemo(() => {
    const total = logs.length;
    const failed = logs.filter((log) =>
      ["failed", "delivery_failed"].includes(
        log.notificationStatus || log.deliveryStatus,
      ),
    ).length;
    const delivered = logs.filter(
      (log) => log.deliveryStatus === "delivered",
    ).length;
    const read = logs.filter((log) => log.deliveryStatus === "read").length;
    const sent = logs.filter((log) =>
      ["sent_to_meta", "sent", "delivered", "read"].includes(
        log.notificationStatus || log.deliveryStatus,
      ),
    ).length;

    return { total, sent, delivered, read, failed };
  }, [logs]);

  const abandonedCartCount = carts.length;
  const recoveredOrders = orders.filter((order) => order.cartId).length;
  const revenue = orders.reduce(
    (sum, order) =>
      sum + Number(order.items_total || order.orderDetails?.amount || 0),
    0,
  );
  const campaignRevenue = campaigns.reduce(
    (sum, campaign) => sum + Number(campaign.revenueGenerated || 0),
    0,
  );

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadCustomersPage = useCallback(
    async (page = audiencePage, pageSize = audienceRowsPerPage) => {
      const response = await engagementRequest(
        `/engagement/customers?page=${page}&pageSize=${pageSize}&includeMetrics=0`,
      );
      setCustomers(response.rows || []);
      setCustomersTotal(Number(response.total || 0));
    },
    [audiencePage, audienceRowsPerPage],
  );

  const loadCampaignsPage = useCallback(
    async (page = campaignPage, pageSize = campaignRowsPerPage) => {
      const response = await engagementRequest(
        `/engagement/campaigns?page=${page}&pageSize=${pageSize}&sinceDays=${sinceDays}`,
      );
      setCampaigns(response.rows || []);
      setCampaignsTotal(Number(response.total || 0));
    },
    [campaignPage, campaignRowsPerPage, sinceDays],
  );

  const loadLogsPage = useCallback(
    async (
      page = logsPage,
      pageSize = logsRowsPerPage,
      nextSearch = search,
      nextStatus = statusFilter,
    ) => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sinceDays: String(sinceDays),
        search: nextSearch || "",
        status: nextStatus || "all",
      });
      const response = await engagementRequest(
        `/engagement/logs?${params.toString()}`,
      );
      setLogs(response.rows || []);
      setLogsTotal(Number(response.total || 0));
    },
    [logsPage, logsRowsPerPage, search, statusFilter, sinceDays],
  );

  const loadAudienceCounts = useCallback(async (preset) => {
    if (preset) setIsAudienceEstimateLoading(true);
    const params = new URLSearchParams();
    if (preset) params.set("preset", preset);
    const query = params.toString();
    try {
      const response = await engagementRequest(
        `/engagement/audience-counts${query ? `?${query}` : ""}`,
      );
      if (response?.counts) {
        setAudienceCounts((prev) => ({ ...prev, ...response.counts }));
        setLoadedAudiencePresets((prev) => ({
          ...prev,
          ...Object.keys(response.counts).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        }));
      }
    } finally {
      if (preset) setIsAudienceEstimateLoading(false);
    }
  }, []);

  const loadData = useCallback(
    async ({ initial = false } = {}) => {
      if (initial) setLoader(true);
      setIsRefreshing(true);
      try {
        const queryNames = [
          "summary",
          "templates",
          "settings",
          "products",
          "categories",
          "imported audiences",
        ];
        const results = await Promise.allSettled([
          engagementRequest(`/engagement/summary?sinceDays=${sinceDays}`),
          getDocs(query(collection(db, "whatsappTemplates"), orderBy("name"))),
          getDoc(doc(db, "engagementSettings", "abandonedCart")),
          getDocs(
            query(
              collection(db, "products"),
              orderBy("product_name"),
              limit(500),
            ),
          ),
          getDocs(
            query(
              collection(db, "categories"),
              orderBy("categoryName"),
              limit(500),
            ),
          ),
          getDocs(
            query(
              collection(db, "whatsappAudiences"),
              orderBy("createdAt", "desc"),
              limit(100),
            ),
          ),
        ]);
        const valueAt = (index) =>
          results[index].status === "fulfilled" ? results[index].value : null;
        const failedQueries = results
          .map((result, index) =>
            result.status === "rejected" ? queryNames[index] : null,
          )
          .filter(Boolean);
        const summaryResponse = valueAt(0);
        const templatesSnap = valueAt(1);
        const settingsSnap = valueAt(2);
        const productsSnap = valueAt(3);
        const categoriesSnap = valueAt(4);
        const audiencesSnap = valueAt(5);

        if (results[0].status === "rejected") {
          console.error("Summary API failed:", results[0].reason);
          showToast("Failed to load engagement summary", "error");
        }

        if (summaryResponse) setSummary(summaryResponse);
        if (templatesSnap) {
          setTemplates(
            mergeDefaultTemplates(
              templatesSnap.docs.map((item) => ({
                id: item.id,
                ...item.data(),
              })),
            ),
          );
        }
        if (productsSnap)
          setProducts(
            productsSnap.docs.map((item) => ({
              docId: item.id,
              ...item.data(),
            })),
          );
        if (categoriesSnap)
          setCategories(
            categoriesSnap.docs.map((item) => ({
              docId: item.id,
              ...item.data(),
            })),
          );
        if (audiencesSnap)
          setImportedAudiences(
            audiencesSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
          );

        if (settingsSnap?.exists()) {
          setSettings((prev) => ({ ...prev, ...settingsSnap.data() }));
        }
        if (failedQueries.length) {
          setSnackbar({
            open: true,
            message: `Some data could not be refreshed: ${failedQueries.join(", ")}`,
            severity: "warning",
          });
        }

        if (activeTab === "Audience") {
          await loadCustomersPage(0, audienceRowsPerPage);
        }
        if (activeTab === "WhatsApp Campaigns") {
          await loadCampaignsPage(0, campaignRowsPerPage);
        }
        if (activeTab === "Message Logs") {
          await loadLogsPage(0, logsRowsPerPage, search, statusFilter);
        }

        if (activeTab === "Audience") {
          loadAudienceCounts().catch((error) => {
            console.error("Audience counts API failed:", error);
          });
        }
        if (activeTab === "WhatsApp Campaigns") {
          loadAudienceCounts(campaignForm.audiencePreset).catch((error) => {
            console.error("Audience estimate API failed:", error);
          });
        }
      } catch (error) {
        console.error("Failed to load customer engagement data:", error);
        setSnackbar({
          open: true,
          message: "Failed to refresh engagement data",
          severity: "error",
        });
      } finally {
        if (initial) setLoader(false);
        setIsRefreshing(false);
      }
    },
    [
      audienceRowsPerPage,
      campaignRowsPerPage,
      loadCampaignsPage,
      loadCustomersPage,
      loadLogsPage,
      activeTab,
      campaignForm.audiencePreset,
      search,
      setLoader,
      statusFilter,
      logsRowsPerPage,
      sinceDays,
      loadAudienceCounts,
    ],
  );

  useEffect(() => {
    setHeaderData({
      leftText: "Customer Engagement",
      rightActionText: "Refresh",
      leftAction: () => {},
      rightAction: () => loadData(),
      back: true,
    });
  }, [loadData, setHeaderData]);

  useEffect(() => {
    loadData({ initial: true });
  }, [loadData]);

  useEffect(() => {
    const subTab = selectedTab?.subCategory?.title;
    if (TABS.includes(subTab)) {
      setActiveTab(subTab);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!campaignForm.templateId && campaignTemplates[0]) {
      setCampaignForm((prev) => ({
        ...prev,
        templateId: campaignTemplates[0].id,
      }));
    }
  }, [campaignTemplates, campaignForm.templateId]);

  useEffect(() => {
    setLogsPage(0);
  }, [search, statusFilter]);

  useEffect(() => {
    setCampaignPage(0);
    setLogsPage(0);
  }, [sinceDays]);

  useEffect(() => {
    if (activeTab !== "Audience") return;
    loadCustomersPage(audiencePage, audienceRowsPerPage).catch((error) => {
      console.error("Failed to load customer page:", error);
    });
  }, [activeTab, audiencePage, audienceRowsPerPage, loadCustomersPage]);

  useEffect(() => {
    if (activeTab !== "WhatsApp Campaigns") return;
    loadCampaignsPage(campaignPage, campaignRowsPerPage).catch((error) => {
      console.error("Failed to load campaign page:", error);
    });
  }, [activeTab, campaignPage, campaignRowsPerPage, loadCampaignsPage]);

  useEffect(() => {
    if (activeTab !== "Message Logs") return;
    loadLogsPage(logsPage, logsRowsPerPage, search, statusFilter).catch(
      (error) => {
        console.error("Failed to load log page:", error);
      },
    );
  }, [
    activeTab,
    logsPage,
    logsRowsPerPage,
    search,
    statusFilter,
    loadLogsPage,
  ]);

  useEffect(() => {
    if (activeTab === "Audience") {
      loadAudienceCounts().catch((error) => {
        console.error("Failed to load audience counts:", error);
      });
      return;
    }

    if (activeTab !== "WhatsApp Campaigns") return;
    loadAudienceCounts(campaignForm.audiencePreset).catch((error) => {
      console.error("Failed to load audience counts:", error);
    });
  }, [activeTab, campaignForm.audiencePreset, loadAudienceCounts]);

  const importedContactsTotal = importedAudiences.reduce(
    (sum, audience) => sum + Number(audience.optedInCount || 0),
    0,
  );

  const selectedTemplate = templates.find(
    (template) => template.id === campaignForm.templateId,
  );
  const selectedImportedAudience = importedAudiences.find(
    (audience) => audience.id === campaignForm.importAudienceId,
  );
  const isImportedEstimate = campaignForm.audiencePreset === "imported";
  const hasLoadedAudienceEstimate =
    isImportedEstimate || !!loadedAudiencePresets[campaignForm.audiencePreset];
  const estimatedRecipients =
    isImportedEstimate
      ? Number(
          selectedImportedAudience?.contactCount ||
            selectedImportedAudience?.optedInCount ||
            0,
        )
      : hasLoadedAudienceEstimate
        ? audienceCounts[campaignForm.audiencePreset] || 0
        : null;
  const selectedProduct = products.find(
    (product) => (product.id || product.docId) === campaignForm.targetProductId,
  );
  const selectedCategory = categories.find(
    (category) =>
      (category.id || category.docId) === campaignForm.targetCategoryId,
  );
  const resolveCampaignTargetUrl = () => {
    if (campaignForm.targetType === "custom")
      return campaignForm.targetUrl.trim();
    if (campaignForm.targetType === "product" && campaignForm.targetProductId) {
      return `https://www.kathysonline.in/product/${campaignForm.targetProductId}`;
    }
    if (
      campaignForm.targetType === "category" &&
      campaignForm.targetCategoryId
    ) {
      return `https://www.kathysonline.in/category/${campaignForm.targetCategoryId}`;
    }
    return "https://www.kathysonline.in";
  };
  const campaignTargetLabel = selectedProduct
    ? getProductName(selectedProduct)
    : selectedCategory?.categoryName ||
      (campaignForm.targetType === "custom"
        ? "Custom landing page"
        : "Kathy's Online");
  const campaignTargetImage = getPreviewImage(
    selectedProduct || selectedCategory,
  );
  const resolvedCreativeImage =
    campaignForm.creativeImageUrl.trim() || campaignTargetImage;

  const createCampaign = async (targetStatus) => {
    if (actionInProgress) return;
    if (isUploadingImage) {
      showToast("Wait for image upload to complete", "error");
      return;
    }
    if (!campaignForm.name.trim()) {
      showToast("Campaign name is required", "error");
      return;
    }

    if (!campaignForm.templateId) {
      showToast("Select an approved WhatsApp template", "error");
      return;
    }

    if (
      campaignForm.audiencePreset === "imported" &&
      !campaignForm.importAudienceId
    ) {
      showToast("Select or import an Excel audience", "error");
      return;
    }

    const status =
      targetStatus ||
      (campaignForm.sendMode === "schedule" ? "scheduled" : "queued");

    if (status === "scheduled" && !campaignForm.scheduledAt) {
      showToast("Schedule time is required", "error");
      return;
    }

    const scheduledDate = campaignForm.scheduledAt
      ? new Date(campaignForm.scheduledAt)
      : null;
    if (
      status === "scheduled" &&
      (!scheduledDate ||
        Number.isNaN(scheduledDate.getTime()) ||
        scheduledDate <= new Date())
    ) {
      showToast("Schedule time must be in the future", "error");
      return;
    }

    if (status !== "draft" && estimatedRecipients === null) {
      showToast("Estimating audience size. Please wait a moment.", "error");
      return;
    }

    if (status !== "draft" && estimatedRecipients <= 0) {
      showToast("This audience has no eligible customers", "error");
      return;
    }

    if (
      campaignForm.targetType === "product" &&
      !campaignForm.targetProductId
    ) {
      showToast("Select a product for this campaign", "error");
      return;
    }

    if (
      campaignForm.targetType === "category" &&
      !campaignForm.targetCategoryId
    ) {
      showToast("Select a category for this campaign", "error");
      return;
    }

    if (
      campaignForm.targetType === "custom" &&
      !campaignForm.targetUrl.trim()
    ) {
      showToast("Enter a custom campaign URL", "error");
      return;
    }

    if (
      campaignForm.targetType === "custom" &&
      !isValidHttpsUrl(campaignForm.targetUrl)
    ) {
      showToast("Custom campaign URL must be a valid HTTPS URL", "error");
      return;
    }

    if (selectedTemplate?.hasImageHeader && !resolvedCreativeImage) {
      showToast(
        "Image-header template requires a creative image URL or selected product/category image",
        "error",
      );
      return;
    }

    if (resolvedCreativeImage && !isValidHttpsUrl(resolvedCreativeImage)) {
      showToast("Creative image URL must be a public HTTPS URL", "error");
      return;
    }

    try {
      setActionInProgress(status === "draft" ? "draft" : "campaign");
      await engagementRequest("/engagement/campaigns", {
        method: "POST",
        body: JSON.stringify({
          ...campaignForm,
          status,
          scheduledAt:
            status === "scheduled" && scheduledDate
              ? scheduledDate.toISOString()
              : null,
          templateName: selectedTemplate?.name || "",
          targetUrl: resolveCampaignTargetUrl(),
          creativeImageUrl: resolvedCreativeImage,
          targetLabel:
            selectedProduct?.product_name ||
            selectedProduct?.name ||
            selectedCategory?.categoryName ||
            campaignForm.targetUrl ||
            "Kathy's Online",
          estimatedRecipients,
        }),
      });
      setCampaignForm(emptyCampaign);
      showToast(
        status === "draft" ? "Campaign saved as draft" : "Campaign queued",
      );
      await loadData();
    } catch (error) {
      console.error("Failed to create campaign:", error);
      showToast(error.message || "Failed to create campaign", "error");
    } finally {
      setActionInProgress("");
    }
  };

  const handleCreativeImageUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please choose a valid image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image must be 5 MB or smaller", "error");
      return;
    }

    const safeName = String(file.name || "creative-image")
      .replace(/[^a-zA-Z0-9._-]/g, "-")
      .slice(0, 120);
    const path = `engagement/campaign-creatives/${Date.now()}_${safeName}`;
    const uploadRef = ref(storage, path);
    const task = uploadBytesResumable(uploadRef, file, {
      contentType: file.type,
      cacheControl: "public,max-age=3600",
    });

    setIsUploadingImage(true);
    setImageUploadProgress(0);

    task.on(
      "state_changed",
      (snapshot) => {
        const progress = snapshot.totalBytes
          ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          : 0;
        setImageUploadProgress(progress);
      },
      (error) => {
        console.error("Failed to upload campaign image:", error);
        setIsUploadingImage(false);
        setImageUploadProgress(0);
        showToast(
          "Failed to upload image. Check Firebase Storage rules.",
          "error",
        );
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(task.snapshot.ref);
          setCampaignForm((prev) => ({
            ...prev,
            creativeImageUrl: downloadUrl,
          }));
          setImageUploadProgress(100);
          showToast("Image uploaded and linked to campaign");
        } catch (error) {
          console.error("Failed to resolve uploaded image URL:", error);
          showToast(
            "Upload finished but image URL could not be resolved",
            "error",
          );
        } finally {
          setIsUploadingImage(false);
        }
      },
    );
  };

  const updateCampaignStatus = async (campaign, status) => {
    if (actionInProgress) return;
    try {
      setActionInProgress(`campaign_${campaign.id}`);
      await engagementRequest(`/engagement/campaigns/${campaign.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      showToast(`Campaign ${status}`);
      await loadData();
    } catch (error) {
      console.error("Failed to update campaign:", error);
      showToast(error.message || "Failed to update campaign", "error");
    } finally {
      setActionInProgress("");
    }
  };

  const normalizeImportRow = (row, index) => {
    const normalized = {};
    Object.entries(row || {}).forEach(([key, value]) => {
      normalized[String(key).trim().toLowerCase()] = value;
    });

    const firstName = normalized["first name"] || normalized.firstname || "";
    const lastName = normalized["last name"] || normalized.lastname || "";
    const name =
      normalized.name ||
      normalized.customer ||
      normalized["customer name"] ||
      [firstName, lastName].filter(Boolean).join(" ");
    const phone =
      normalized.phone ||
      normalized.mobile ||
      normalized["mobile number"] ||
      normalized["phone number"] ||
      normalized.whatsapp ||
      normalized["whatsapp phone"] ||
      normalized["whatsapp number"];
    const email = normalized.email || normalized["email address"] || "";
    const optInValue = String(
      normalized["whatsapp opt in"] ||
        normalized["opt in"] ||
        normalized["whatsapp consent"] ||
        normalized.consent ||
        "",
    )
      .trim()
      .toLowerCase();
    const optOutValue = String(normalized.optout || normalized["opt out"] || "")
      .trim()
      .toLowerCase();
    const explicitlyOptedIn = ["yes", "true", "1", "y", "opted in"].includes(
      optInValue,
    );
    const explicitlyOptedOut = ["yes", "true", "1", "y"].includes(optOutValue);

    return {
      rowNumber: index + 2,
      name: String(name || "Customer").trim(),
      phone: String(phone || "").replace(/[^\d+]/g, ""),
      email: String(email || "").trim(),
      whatsappOptIn: !(optOutValue && explicitlyOptedOut) || explicitlyOptedIn,
      consentSource: "import_any_valid_phone",
    };
  };

  const handleAudienceFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast("Audience file must be 5 MB or smaller", "error");
      event.target.value = "";
      return;
    }

    try {
      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
      if (rows.length > 10000) {
        throw new Error("Audience files are limited to 10,000 rows");
      }
      const parsedContacts = rows
        .map(normalizeImportRow)
        .filter((contact) => contact.phone.replace(/\D/g, "").length >= 10);
      const contactsByPhone = new Map();
      parsedContacts.forEach((contact) => {
        const phoneKey = contact.phone.replace(/\D/g, "");
        if (!contactsByPhone.has(phoneKey))
          contactsByPhone.set(phoneKey, contact);
      });
      const contacts = Array.from(contactsByPhone.values());

      setImportContacts(contacts);
      setImportName(file.name.replace(/\.(xlsx|xls|csv)$/i, ""));
      if (!contacts.length) {
        showToast("No valid phone numbers found in the sheet", "error");
      }
    } catch (error) {
      console.error("Failed to parse audience file:", error);
      showToast("Failed to read Excel/CSV file", "error");
    } finally {
      event.target.value = "";
    }
  };

  const handleDownloadAudienceTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      const templateRows = [
        {
          Name: "Anu Nair",
          "First Name": "Anu",
          "Last Name": "Nair",
          Phone: "+919876543210",
          Mobile: "+919876543210",
          "Phone Number": "+919876543210",
          "WhatsApp Number": "+919876543210",
          Email: "anu@example.com",
          "WhatsApp Opt In": "Yes",
          "Opt Out": "No",
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(templateRows);
      worksheet["!cols"] = [
        { wch: 22 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 28 },
        { wch: 18 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Audience Template",
      );

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "customer_audience_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to generate audience template:", error);
      showToast("Failed to download Excel template", "error");
    }
  };

  const saveImportedAudience = async () => {
    if (actionInProgress) return;
    if (!importName.trim()) {
      showToast("Audience name is required", "error");
      return;
    }
    if (!importContacts.length) {
      showToast("Import a sheet with valid phone numbers first", "error");
      return;
    }

    try {
      setActionInProgress("import");
      const importedAudience = await engagementRequest(
        "/engagement/audiences",
        {
          method: "POST",
          body: JSON.stringify({
            name: importName.trim(),
            contacts: importContacts,
          }),
        },
      );

      setCampaignForm((prev) => ({
        ...prev,
        audiencePreset: "imported",
        importAudienceId: importedAudience.id,
      }));
      setImportDialogOpen(false);
      setImportContacts([]);
      setImportName("");
      showToast("Imported audience saved");
      await loadData();
    } catch (error) {
      console.error("Failed to save imported audience:", error);
      showToast(error.message || "Failed to save imported audience", "error");
    } finally {
      setActionInProgress("");
    }
  };

  const saveTemplate = async () => {
    if (actionInProgress) return;
    if (!templateForm.name.trim() || !templateForm.body.trim()) {
      showToast("Template name and body are required", "error");
      return;
    }

    try {
      setActionInProgress("template");
      await engagementRequest("/engagement/templates", {
        method: "POST",
        body: JSON.stringify(templateForm),
      });
      setTemplateDialogOpen(false);
      showToast("Template saved");
      await loadData();
    } catch (error) {
      console.error("Failed to save template:", error);
      showToast(error.message || "Failed to save template", "error");
    } finally {
      setActionInProgress("");
    }
  };

  const syncMetaTemplates = async () => {
    if (actionInProgress) return;
    try {
      setActionInProgress("sync_templates");
      const result = await engagementRequest("/engagement/templates/sync", {
        method: "POST",
      });
      showToast(
        `Template sync completed: ${Number(result.syncedCount || 0)} synced`,
        "success",
      );
      await loadData();
    } catch (error) {
      console.error("Failed to sync templates from Meta:", error);
      showToast(error.message || "Failed to sync templates", "error");
    } finally {
      setActionInProgress("");
    }
  };

  const saveSettings = async () => {
    if (actionInProgress) return;
    if (settings.frequencyLimitDays < 1 || settings.frequencyLimitDays > 90) {
      showToast("Frequency limit must be between 1 and 90 days", "error");
      return;
    }
    try {
      setActionInProgress("settings");
      await engagementRequest("/engagement/settings", {
        method: "PUT",
        body: JSON.stringify({ ...settings, respectOptOut: false }),
      });
      showToast("Engagement settings saved");
      await loadData();
    } catch (error) {
      console.error("Failed to save settings:", error);
      showToast(error.message || "Failed to save settings", "error");
    } finally {
      setActionInProgress("");
    }
  };

  const renderOverview = () => (
    <>
      <div className="engagement-grid">
        <MetricCard
          title="Campaigns"
          value={summary.totalCampaigns}
          meta="All campaign records"
        />
        <MetricCard
          title="Recipients queued"
          value={summary.queuedRecipients}
          meta="Across all campaigns"
        />
        <MetricCard
          title="WhatsApp sent"
          value={summary.sentMessages}
          meta={`${summary.deliveredMessages} delivered · ${summary.readMessages} read`}
        />
        <MetricCard
          title="Campaign revenue"
          value={formatCurrency(summary.totalCampaignRevenue)}
          meta="Requires campaign attribution"
        />
      </div>

      <div className="engagement-section">
        <div className="engagement-section-header">
          <div>
            <h2>Engagement Health</h2>
            <p className="engagement-muted">
              Backend-driven totals and current engagement counts.
            </p>
          </div>
        </div>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Customers</TableCell>
                <TableCell align="right">{summary.totalCustomers}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Abandoned cart candidates</TableCell>
                <TableCell align="right">
                  {summary.abandonedCartCandidates}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Delivered messages</TableCell>
                <TableCell align="right">{summary.deliveredMessages}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Read messages</TableCell>
                <TableCell align="right">{summary.readMessages}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Failed messages</TableCell>
                <TableCell align="right">{summary.failedMessages}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Orders with cartId</TableCell>
                <TableCell align="right">{summary.ordersWithCart}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );

  const renderCampaigns = () => (
    <>
      <div className="engagement-section">
        <div className="engagement-section-header">
          <div>
            <h2>Create WhatsApp Campaign</h2>
            <p className="engagement-muted">
              Campaigns are queued in Firestore for server-side processing.
            </p>
          </div>
          <Chip
            label={
              estimatedRecipients === null
                ? "Estimating recipients..."
                : `${estimatedRecipients} estimated recipients`
            }
            color={estimatedRecipients === null ? "warning" : "primary"}
          />
        </div>
        <div className="engagement-form-grid">
          <TextField
            label="Campaign name"
            value={campaignForm.name}
            onChange={(e) =>
              setCampaignForm({ ...campaignForm, name: e.target.value })
            }
            size="small"
          />
          <FormControl size="small">
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={campaignForm.templateId}
              onChange={(e) =>
                setCampaignForm({ ...campaignForm, templateId: e.target.value })
              }
            >
              {campaignTemplates.length === 0 && (
                <MenuItem value="" disabled>
                  No active marketing templates
                </MenuItem>
              )}
              {campaignTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Audience</InputLabel>
            <Select
              label="Audience"
              value={campaignForm.audiencePreset}
              onChange={(e) =>
                setCampaignForm({
                  ...campaignForm,
                  audiencePreset: e.target.value,
                })
              }
            >
              {AUDIENCE_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {campaignForm.audiencePreset === "imported" && (
            <FormControl size="small">
              <InputLabel>Imported audience</InputLabel>
              <Select
                label="Imported audience"
                value={campaignForm.importAudienceId}
                onChange={(e) =>
                  setCampaignForm({
                    ...campaignForm,
                    importAudienceId: e.target.value,
                  })
                }
              >
                {importedAudiences.map((audience) => (
                  <MenuItem key={audience.id} value={audience.id}>
                    {audience.name} ({audience.contactCount || 0})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small">
            <InputLabel>Send mode</InputLabel>
            <Select
              label="Send mode"
              value={campaignForm.sendMode}
              onChange={(e) =>
                setCampaignForm({ ...campaignForm, sendMode: e.target.value })
              }
            >
              <MenuItem value="now">Send immediately</MenuItem>
              <MenuItem value="schedule">Schedule</MenuItem>
            </Select>
          </FormControl>
          {campaignForm.sendMode === "schedule" && (
            <TextField
              type="datetime-local"
              label="Schedule time"
              InputLabelProps={{ shrink: true }}
              value={campaignForm.scheduledAt}
              onChange={(e) =>
                setCampaignForm({
                  ...campaignForm,
                  scheduledAt: e.target.value,
                })
              }
              size="small"
            />
          )}
          <FormControl size="small">
            <InputLabel>Campaign destination</InputLabel>
            <Select
              label="Campaign destination"
              value={campaignForm.targetType}
              onChange={(e) =>
                setCampaignForm({
                  ...campaignForm,
                  targetType: e.target.value,
                  targetProductId: "",
                  targetCategoryId: "",
                  targetUrl: "",
                })
              }
            >
              <MenuItem value="general">Homepage / general</MenuItem>
              <MenuItem value="product">Specific product</MenuItem>
              <MenuItem value="category">Specific category</MenuItem>
              <MenuItem value="custom">Custom URL</MenuItem>
            </Select>
          </FormControl>
          {campaignForm.targetType === "product" && (
            <Autocomplete
              size="small"
              options={products}
              value={selectedProduct || null}
              getOptionLabel={(product) =>
                product.product_name ||
                product.name ||
                product.id ||
                product.docId ||
                ""
              }
              isOptionEqualToValue={(option, value) =>
                (option.id || option.docId) === (value.id || value.docId)
              }
              onChange={(event, product) =>
                setCampaignForm({
                  ...campaignForm,
                  targetProductId: product ? product.id || product.docId : "",
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Product" />
              )}
            />
          )}
          {campaignForm.targetType === "category" && (
            <Autocomplete
              size="small"
              options={categories}
              value={selectedCategory || null}
              getOptionLabel={(category) =>
                category.categoryName || category.id || category.docId || ""
              }
              isOptionEqualToValue={(option, value) =>
                (option.id || option.docId) === (value.id || value.docId)
              }
              onChange={(event, category) =>
                setCampaignForm({
                  ...campaignForm,
                  targetCategoryId: category
                    ? category.id || category.docId
                    : "",
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Category" />
              )}
            />
          )}
          {campaignForm.targetType === "custom" && (
            <TextField
              label="Custom campaign URL"
              value={campaignForm.targetUrl}
              onChange={(e) =>
                setCampaignForm({ ...campaignForm, targetUrl: e.target.value })
              }
              size="small"
              placeholder="https://www.kathysonline.in/category/..."
            />
          )}
          <TextField
            className="engagement-form-full"
            label="Campaign message"
            value={campaignForm.campaignMessage}
            onChange={(e) =>
              setCampaignForm({
                ...campaignForm,
                campaignMessage: e.target.value,
              })
            }
            size="small"
            multiline
            minRows={3}
            placeholder="Example: New arrivals are live. Tap to shop the latest Kathy's collection."
          />
          <TextField
            className="engagement-form-full"
            label="Creative image URL"
            value={campaignForm.creativeImageUrl}
            onChange={(e) =>
              setCampaignForm({
                ...campaignForm,
                creativeImageUrl: e.target.value,
              })
            }
            size="small"
            placeholder="Optional. If blank, product/category image is used when available."
          />
          <div className="engagement-form-full engagement-upload-row">
            <Button
              variant="outlined"
              component="label"
              disabled={isUploadingImage || !!actionInProgress}
            >
              {isUploadingImage
                ? "Uploading image..."
                : "Upload image from device"}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleCreativeImageUpload}
              />
            </Button>
            {(isUploadingImage || imageUploadProgress > 0) && (
              <div className="engagement-upload-progress">
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, imageUploadProgress))}
                />
                <Typography variant="caption" color="text.secondary">
                  {imageUploadProgress}%
                </Typography>
              </div>
            )}
            <Alert severity="info">
              Upload supports image files up to 5 MB and automatically fills the
              creative URL.
            </Alert>
          </div>
          <TextField
            className="engagement-form-full"
            label="Dynamic variables"
            value={campaignForm.variables}
            onChange={(e) =>
              setCampaignForm({ ...campaignForm, variables: e.target.value })
            }
            size="small"
          />
          <div className="engagement-destination-card engagement-form-full">
            <div className="engagement-destination-media">
              {resolvedCreativeImage ? (
                <img src={resolvedCreativeImage} alt={campaignTargetLabel} />
              ) : (
                <span>{campaignTargetLabel.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <p className="engagement-card-title">Campaign destination</p>
              <h3>{campaignTargetLabel}</h3>
              {selectedProduct && getProductPrice(selectedProduct) && (
                <p className="engagement-muted">
                  {formatCurrency(getProductPrice(selectedProduct))}
                </p>
              )}
              <a
                href={resolveCampaignTargetUrl()}
                target="_blank"
                rel="noreferrer"
              >
                {resolveCampaignTargetUrl()}
              </a>
            </div>
          </div>
          <div className="engagement-message-preview engagement-form-full">
            <div className="engagement-whatsapp-bubble">
              {selectedTemplate?.body ||
                "Select an approved template to preview the WhatsApp message."}
              {campaignForm.campaignMessage && (
                <>
                  {"\n\nCampaign message: "}
                  {campaignForm.campaignMessage}
                </>
              )}
            </div>
          </div>
        </div>
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            disabled={!!actionInProgress}
            onClick={() => setImportDialogOpen(true)}
          >
            Import Excel Audience
          </Button>
          <Button
            variant="outlined"
            disabled={!!actionInProgress}
            onClick={() => createCampaign("draft")}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            disabled={!!actionInProgress}
            startIcon={
              actionInProgress === "campaign" ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            onClick={() => createCampaign()}
          >
            Queue Campaign
          </Button>
        </Box>
      </div>

      <CampaignTable
        campaigns={campaigns}
        total={campaignsTotal}
        page={campaignPage}
        rowsPerPage={campaignRowsPerPage}
        onPageChange={setCampaignPage}
        onRowsPerPageChange={(value) => {
          setCampaignRowsPerPage(value);
          setCampaignPage(0);
        }}
        onStatusChange={updateCampaignStatus}
        onCancel={setCampaignToCancel}
        actionInProgress={actionInProgress}
      />
    </>
  );

  const renderAbandonedCart = () => (
    <div className="engagement-section">
      <div className="engagement-section-header">
        <div>
          <h2>Abandoned Cart Recovery</h2>
          <p className="engagement-muted">
            Configure recovery steps. Backend senders must verify cart state
            before each message.
          </p>
        </div>
        <Chip
          label={`${summary.abandonedCartCandidates} cart candidates`}
          color="warning"
        />
      </div>
      <div className="engagement-form-grid">
        <TextField
          label="Trigger value"
          type="number"
          size="small"
          value={settings.abandonedCartTriggerValue}
          onChange={(e) =>
            setSettings({
              ...settings,
              abandonedCartTriggerValue: Number(e.target.value),
            })
          }
        />
        <FormControl size="small">
          <InputLabel>Trigger unit</InputLabel>
          <Select
            label="Trigger unit"
            value={settings.abandonedCartTriggerUnit}
            onChange={(e) =>
              setSettings({
                ...settings,
                abandonedCartTriggerUnit: e.target.value,
              })
            }
          >
            <MenuItem value="minutes">Minutes</MenuItem>
            <MenuItem value="hours">Hours</MenuItem>
          </Select>
        </FormControl>
      </div>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
        {(settings.steps || DEFAULT_ABANDONED_STEPS).map((step, index) => (
          <div className="engagement-card" key={index}>
            <div className="engagement-form-grid">
              <TextField
                label="Delay"
                type="number"
                size="small"
                value={step.delayValue}
                onChange={(e) =>
                  updateStep(index, { delayValue: Number(e.target.value) })
                }
              />
              <FormControl size="small">
                <InputLabel>Unit</InputLabel>
                <Select
                  label="Unit"
                  value={step.delayUnit}
                  onChange={(e) =>
                    updateStep(index, { delayUnit: e.target.value })
                  }
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Template name"
                size="small"
                value={step.templateName}
                onChange={(e) =>
                  updateStep(index, { templateName: e.target.value })
                }
              />
              <TextField
                label="Optional discount code"
                size="small"
                value={step.discountCode}
                onChange={(e) =>
                  updateStep(index, { discountCode: e.target.value })
                }
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={!!step.enabled}
                  onChange={(e) =>
                    updateStep(index, { enabled: e.target.checked })
                  }
                />
                <Typography>{step.enabled ? "Enabled" : "Disabled"}</Typography>
              </Box>
            </div>
          </div>
        ))}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={saveSettings}>
          Save abandoned-cart settings
        </Button>
      </Box>
    </div>
  );

  const updateStep = (index, patch) => {
    setSettings((prev) => {
      const steps = [...(prev.steps || DEFAULT_ABANDONED_STEPS)];
      steps[index] = { ...steps[index], ...patch };
      return { ...prev, steps };
    });
  };

  const renderTemplates = () => (
    <div className="engagement-section">
      <div className="engagement-section-header">
        <div>
          <h2>WhatsApp Templates</h2>
          <p className="engagement-muted">
            Mirror approved Meta templates here. Only approved templates are
            available for campaigns.
          </p>
        </div>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            disabled={!!actionInProgress}
            startIcon={
              actionInProgress === "sync_templates" ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            onClick={syncMetaTemplates}
          >
            Sync Meta Templates
          </Button>
          <Button
            variant="contained"
            disabled={!!actionInProgress}
            onClick={() => setTemplateDialogOpen(true)}
          >
            Add Template
          </Button>
        </Box>
      </div>
      {templates.length === 0 ? (
        <div className="engagement-empty">
          No templates saved yet. Add `kathys_order_status` after Meta approval.
        </div>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Media</TableCell>
                <TableCell>Variables</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>{template.category}</TableCell>
                  <TableCell>{template.language}</TableCell>
                  <TableCell>
                    <StatusPill value={template.approvalStatus} />
                  </TableCell>
                  <TableCell>
                    {template.hasImageHeader ? "Image header" : "Text only"}
                  </TableCell>
                  <TableCell>{template.variables}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );

  const renderAudience = () => (
    <div className="engagement-section">
      <div className="engagement-section-header">
        <div>
          <h2>Audience</h2>
          <p className="engagement-muted">
            Segments are estimated from existing customer, order, cart, and
            WhatsApp message data.
          </p>
        </div>
        <Button variant="contained" onClick={() => setImportDialogOpen(true)}>
          Import Excel Audience
        </Button>
      </div>
      <div className="engagement-grid">
        {AUDIENCE_PRESETS.map((preset) => (
          <MetricCard
            key={preset.value}
            title={preset.label}
            value={
              preset.value === "imported"
                ? importedContactsTotal
                : audienceCounts[preset.value] || 0
            }
          />
        ))}
      </div>
      {importedAudiences.length > 0 && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Imported audience</TableCell>
                <TableCell align="right">Eligible / total</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {importedAudiences.map((audience) => (
                <TableRow key={audience.id}>
                  <TableCell>{audience.name}</TableCell>
                  <TableCell align="right">
                    {audience.optedInCount || 0} / {audience.contactCount || 0}
                  </TableCell>
                  <TableCell>{formatDate(audience.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell align="right">Spend</TableCell>
              <TableCell align="right">Orders</TableCell>
              <TableCell>Last order</TableCell>
              <TableCell>WhatsApp flag</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell>
                  {customer.name || getCustomerName(customer)}
                </TableCell>
                <TableCell>
                  {customer.phone || getCustomerPhone(customer) || "N/A"}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(customer.totalSpend)}
                </TableCell>
                <TableCell align="right">{customer.totalOrders}</TableCell>
                <TableCell>
                  {customer.lastOrderDate
                    ? formatDate(customer.lastOrderDate)
                    : "N/A"}
                </TableCell>
                <TableCell>{customer.whatsappOptIn ? "Yes" : "N/A"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={customersTotal}
          page={audiencePage}
          onPageChange={(event, newPage) => setAudiencePage(newPage)}
          rowsPerPage={audienceRowsPerPage}
          onRowsPerPageChange={(event) => {
            setAudienceRowsPerPage(parseInt(event.target.value, 10));
            setAudiencePage(0);
          }}
          rowsPerPageOptions={[100, 200, 1000]}
        />
      </TableContainer>
    </div>
  );

  const renderLogs = () => (
    <div className="engagement-section">
      <div className="engagement-section-header">
        <div>
          <h2>Message Logs</h2>
          <p className="engagement-muted">
            Search WhatsApp delivery records from `whatsappNotifications`.
          </p>
        </div>
      </div>
      <div className="engagement-form-grid">
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl size="small">
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="sent_to_meta">Sent</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="read">Read</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </div>
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer / Phone</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Related order</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  {log.customerName || log.maskedPhone || "N/A"}
                </TableCell>
                <TableCell>
                  {log.campaignName || log.campaignId || "Order status"}
                </TableCell>
                <TableCell>{log.templateName || "N/A"}</TableCell>
                <TableCell>
                  {log.messageType || log.channel || "WhatsApp"}
                </TableCell>
                <TableCell>
                  {formatDate(log.createdAt || log.sentToMetaAt)}
                </TableCell>
                <TableCell>
                  <StatusPill
                    value={log.deliveryStatus || log.notificationStatus}
                  />
                </TableCell>
                <TableCell>{log.orderId || "N/A"}</TableCell>
                <TableCell>
                  {log.error?.message || log.deliveryError?.[0]?.title || ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={logsTotal}
          page={logsPage}
          onPageChange={(event, newPage) => setLogsPage(newPage)}
          rowsPerPage={logsRowsPerPage}
          onRowsPerPageChange={(event) => {
            setLogsRowsPerPage(parseInt(event.target.value, 10));
            setLogsPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </div>
  );

  const renderSettings = () => (
    <div className="engagement-section">
      <div className="engagement-section-header">
        <div>
          <h2>Settings</h2>
          <p className="engagement-muted">
            Global controls for suppression and send safety.
          </p>
        </div>
      </div>
      <div className="engagement-form-grid">
        <TextField
          label="Frequency limit days"
          type="number"
          size="small"
          inputProps={{ min: 1, max: 90 }}
          value={settings.frequencyLimitDays}
          onChange={(e) =>
            setSettings({
              ...settings,
              frequencyLimitDays: Number(e.target.value),
            })
          }
        />
        <Alert severity="info">
          Campaign and abandoned-cart audiences now use valid customer/cart
          phone data without consent filtering.
        </Alert>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch
            checked={!!settings.abandonedCartEnabled}
            onChange={(e) =>
              setSettings({
                ...settings,
                abandonedCartEnabled: e.target.checked,
              })
            }
          />
          <Typography>Enable abandoned-cart recovery</Typography>
        </Box>
      </div>
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={!!actionInProgress}
          onClick={saveSettings}
        >
          Save settings
        </Button>
      </Box>
    </div>
  );

  const renderActiveTab = () => {
    if (activeTab === "Overview") return renderOverview();
    if (activeTab === "WhatsApp Campaigns") return renderCampaigns();
    if (activeTab === "Abandoned Cart") return renderAbandonedCart();
    if (activeTab === "Templates") return renderTemplates();
    if (activeTab === "Audience") return renderAudience();
    if (activeTab === "Message Logs") return renderLogs();
    if (activeTab === "Settings") return renderSettings();
    return null;
  };

  return (
    <div className="engagement-root">
      {isRefreshing && (
        <LinearProgress aria-label="Refreshing engagement data" />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Data window</InputLabel>
          <Select
            label="Data window"
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
          >
            {DATE_WINDOW_OPTIONS.map((days) => (
              <MenuItem key={days} value={days}>
                Last {days} days
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Customer engagement sections"
        >
          {TABS.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={tab}
              sx={{ textTransform: "none", fontWeight: 400 }}
            />
          ))}
        </Tabs>
      </Box>

      {renderActiveTab()}

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add WhatsApp Template</DialogTitle>
        <DialogContent>
          <div className="engagement-form-grid" style={{ marginTop: 8 }}>
            <TextField
              label="Template name"
              size="small"
              value={templateForm.name}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, name: e.target.value })
              }
            />
            <TextField
              label="Category"
              size="small"
              value={templateForm.category}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, category: e.target.value })
              }
            />
            <TextField
              label="Language"
              size="small"
              value={templateForm.language}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, language: e.target.value })
              }
            />
            <FormControl size="small">
              <InputLabel>Approval status</InputLabel>
              <Select
                disabled
                label="Approval status"
                value={templateForm.approvalStatus}
              >
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
            <TextField
              className="engagement-form-full"
              label="Header"
              size="small"
              value={templateForm.header}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, header: e.target.value })
              }
            />
            <TextField
              className="engagement-form-full"
              label="Body"
              multiline
              minRows={6}
              value={templateForm.body}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, body: e.target.value })
              }
            />
            <TextField
              label="Footer"
              size="small"
              value={templateForm.footer}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, footer: e.target.value })
              }
            />
            <TextField
              label="Buttons"
              size="small"
              value={templateForm.buttons}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, buttons: e.target.value })
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Switch
                checked={!!templateForm.hasImageHeader}
                onChange={(e) =>
                  setTemplateForm({
                    ...templateForm,
                    hasImageHeader: e.target.checked,
                  })
                }
              />
              <Typography>Meta template has image header</Typography>
            </Box>
            <TextField
              className="engagement-form-full"
              label="Variables"
              size="small"
              value={templateForm.variables}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, variables: e.target.value })
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={actionInProgress === "template"}
            onClick={() => setTemplateDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!!actionInProgress}
            onClick={saveTemplate}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Customer Audience</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Audience name"
              size="small"
              value={importName}
              onChange={(e) => setImportName(e.target.value)}
            />
            <Button variant="outlined" component="label">
              Upload Excel / CSV
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleAudienceFile}
              />
            </Button>
            <Button variant="text" onClick={handleDownloadAudienceTemplate}>
              Download Excel Template
            </Button>
            <Alert severity="info">
              Supported columns: Name, First Name, Last Name, Phone, Mobile,
              Phone Number, WhatsApp Number, Email, WhatsApp Opt In, and Opt
              Out. Any row with a valid phone number can be imported for
              messaging.
            </Alert>
            <div className="engagement-preview">
              Valid contacts found: {importContacts.length}
              {`\nRows with valid phones: ${importContacts.length}`}
              {importContacts[0]
                ? `\nSample: ${importContacts[0].name} - ${importContacts[0].phone}`
                : ""}
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={actionInProgress === "import"}
            onClick={() => setImportDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!!actionInProgress || importContacts.length === 0}
            startIcon={
              actionInProgress === "import" ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            onClick={saveImportedAudience}
          >
            Save Audience
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!campaignToCancel}
        onClose={() => setCampaignToCancel(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Any recipients already sent will not be recalled. Remaining
            recipients will be stopped.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignToCancel(null)}>
            Keep campaign
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!actionInProgress}
            onClick={async () => {
              const campaign = campaignToCancel;
              setCampaignToCancel(null);
              await updateCampaignStatus(campaign, "cancelled");
            }}
          >
            Cancel campaign
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const MetricCard = ({ title, value, meta }) => (
  <div className="engagement-card">
    <p className="engagement-card-title">{title}</p>
    <p className="engagement-card-value">{value}</p>
    {meta && <p className="engagement-card-meta">{meta}</p>}
  </div>
);

const CampaignTable = ({
  campaigns,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onStatusChange,
  onCancel,
  actionInProgress,
}) => {
  return (
    <div className="engagement-section">
      <div className="engagement-section-header">
        <div>
          <h2>Campaigns</h2>
          <p className="engagement-muted">
            Pause, resume, or cancel queued campaign records.
          </p>
        </div>
      </div>
      {campaigns.length === 0 ? (
        <div className="engagement-empty">
          No WhatsApp campaigns created yet.
        </div>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Delivered</TableCell>
                <TableCell>Read</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{campaign.templateName || "N/A"}</TableCell>
                  <TableCell>{campaign.audiencePreset}</TableCell>
                  <TableCell>{campaign.estimatedRecipients || 0}</TableCell>
                  <TableCell>{campaign.deliveredCount || 0}</TableCell>
                  <TableCell>{campaign.readCount || 0}</TableCell>
                  <TableCell>
                    <StatusPill value={campaign.status} />
                  </TableCell>
                  <TableCell>{formatDate(campaign.createdAt)}</TableCell>
                  <TableCell>
                    <div className="engagement-actions">
                      {campaign.status === "draft" && (
                        <Button
                          size="small"
                          disabled={!!actionInProgress}
                          onClick={() => onStatusChange(campaign, "queued")}
                        >
                          Queue
                        </Button>
                      )}
                      {campaign.status === "paused" && (
                        <Button
                          size="small"
                          disabled={!!actionInProgress}
                          onClick={() => onStatusChange(campaign, "processing")}
                        >
                          Resume
                        </Button>
                      )}
                      {["queued", "processing", "scheduled"].includes(
                        campaign.status,
                      ) && (
                        <Button
                          size="small"
                          disabled={!!actionInProgress}
                          onClick={() => onStatusChange(campaign, "paused")}
                        >
                          Pause
                        </Button>
                      )}
                      {!["completed", "cancelled"].includes(
                        campaign.status,
                      ) && (
                        <Button
                          size="small"
                          color="error"
                          disabled={!!actionInProgress}
                          onClick={() => onCancel(campaign)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(event, newPage) => onPageChange(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              onRowsPerPageChange(parseInt(event.target.value, 10));
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TableContainer>
      )}
    </div>
  );
};

export default CustomerEngagementTab;
